// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    BillToModel,
    CountryModel,
    CustomerValidationDto,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";
import merge from "lodash/merge";

export type StepModel = {
    index: number;
    name: string;
    displayName: string;
    stepNumber: string;
    prev: number;
    next?: number;
};

export type BasicInfoModel = {
    firstName: string;
    lastName: string;
    email: string;
    userName: string;
    phone: string;
    password: string;
    confirmPassword: string;
};

export type ShippingAndBillingModel = {
    billTo: BillToModel;
    shipTo: ShipToModel;
    shipToSameAsBillTo?: boolean;
};

export type CreateAccountState = {
    isSubmitting: boolean;
    accountInfo: {
        basicInfo: BasicInfoModel;
        billingAndShippingInfo: ShippingAndBillingModel;
    };
};

const initialXCreateAccountState: CreateAccountState = {
    isSubmitting: false,
    accountInfo: {
        basicInfo: {
            firstName: "",
            lastName: "",
            email: "",
            userName: "",
            phone: "",
            password: "",
            confirmPassword: "",
        },
        billingAndShippingInfo: {
            billTo: {} as BillToModel,
            shipTo: {} as ShipToModel,
            shipToSameAsBillTo: true,
        },
    },
};

const xCreateAccount = createTypedReducerWithImmer(initialXCreateAccountState, {
    "xCreateAccount/UpdateBasicInfoForm": (draft: Draft<CreateAccountState>, action: { [key: string]: string }) => {
        const { type, ...payload } = action;
        draft.accountInfo.basicInfo = { ...draft.accountInfo.basicInfo, ...payload };
    },
    "xCreateAccount/SetInitialValues": (
        draft: Draft<CreateAccountState>,
        action: {
            country: CountryModel | undefined;
            billToValidation: CustomerValidationDto | null;
            shipToValidation: CustomerValidationDto | null;
        },
    ) => {
        const { country, billToValidation, shipToValidation } = action;
        const { billTo, shipTo } = draft.accountInfo.billingAndShippingInfo;
        if (country) {
            billTo.country = country;
            shipTo.country = country;
        }
        if (action.billToValidation || action.shipToValidation) {
            billTo.validation = billToValidation;
            shipTo.validation = shipToValidation;
        }
    },
    "xCreateAccount/SetBillingAddress": (
        draft: Draft<CreateAccountState>,
        action: { address: Partial<BillToModel> },
    ) => {
        draft.accountInfo = merge(draft.accountInfo, {
            billingAndShippingInfo: {
                billTo: action.address,
            },
        });
    },
    "xCreateAccount/SetShippingAddress": (
        draft: Draft<CreateAccountState>,
        action: { address: Partial<ShipToModel> },
    ) => {
        draft.accountInfo = merge(draft.accountInfo, {
            billingAndShippingInfo: {
                shipTo: action.address,
            },
        });
    },
    "xCreateAccount/IsShipToSameAsBillTo": (
        draft: Draft<CreateAccountState>,
        action: { isShipToSameAsBillTo: boolean },
    ) => {
        draft.accountInfo.billingAndShippingInfo.shipToSameAsBillTo = action.isShipToSameAsBillTo;
    },
});

(reducers as any).xCreateAccount = xCreateAccount;

export type xCreateAccountState = {
    xCreateAccount: CreateAccountState;
} & ApplicationState;

type xCreateAccountReducers = {
    CreateAccount: typeof xCreateAccount;
};

export type xCreateAccountActions = Parameters<xCreateAccountReducers[keyof xCreateAccountReducers]>[1] | AnyAction;
